import React, { useEffect, useState } from 'react';
import {
  View,
  TouchableOpacity,
  TextInput,
  FlatList,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import getPlatformType from '../../helpers/helpers';
import Colors from '../../constants/Colors';
import drawerHoc from '../../components/drawerHOC';
import Urls from '../../services/Urls';
import ajax from '../../helpers/ajaxHelper';
import moment from 'moment';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';
import CalenderTextSlider from '../../components/calenderTextSlider';
import SortList from '../../components/sortList';
import _ from 'lodash';

var sortData = [
  { name: 'Amount', value: 'amount' },
  { name: 'Date', value: 'date' },
];

/* Status Badge helper matching Figma */
const PaymentStatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  let bg = '#FEF3C7', color = '#D97706', label = 'Pending';

  if (s === 'received' || s === 'paid' || s === 'yes') {
    bg = '#D1FAE5'; color = '#059669'; label = 'Received';
  } else if (s === 'overdue') {
    bg = '#FEE2E2'; color = '#DC2626'; label = 'Overdue';
  } else if (s === 'pending' || s === 'upcoming' || s === 'no') {
    bg = '#FEF3C7'; color = '#D97706'; label = 'Pending';
  } else {
    label = status || 'Pending';
  }

  return (
    <View style={[sb.wrap, { backgroundColor: bg }]}>
      <Text style={[sb.txt, { color }]}>{label}</Text>
    </View>
  );
};
const sb = StyleSheet.create({
  wrap: { borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3, alignSelf: 'flex-start' },
  txt: { fontSize: 11, fontWeight: '600' },
});

const Payment = () => {
  const [payments, setPayments] = useState([]);
  const [incomingPayments, setIncomingPayments] = useState([]);
  const [outgoingPayments, setOutgoingPayments] = useState([]);
  const [filteredIncomingPayments, setFilteredIncomingPayments] = useState([]);
  const [filteredOutgoingPayments, setFilteredOutgoingPayments] = useState([]);
  const [incomingSearch, setIncomingSearch] = useState('');
  const [outgoingSearch, setOutgoingSearch] = useState('');
  const [incomingSort, setIncomingSort] = useState('Date');
  const [outgoingSort, setOutgoingSort] = useState('Date');

  const focus = useIsFocused();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  useEffect(() => {
    const todayMonth = new Date().getMonth() + 1;
    const todayYear = new Date().getFullYear();
    ajax
      .get(`${Urls.Payments}/${todayMonth}/${todayYear}`)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          setPayments(data?.payload);
          const inc = data?.payload.filter((item) => item.is_incoming);
          const out = data?.payload.filter((item) => !item.is_incoming);
          setIncomingPayments(inc);
          setOutgoingPayments(out);
          setFilteredIncomingPayments(inc);
          setFilteredOutgoingPayments(out);
        }
      })
      .catch((error) => {
        console.log('error fetching payments', error);
      });
  }, [focus]);

  /* Calendar Month/Year Change Handlers */
  const setIncomingMonthYear = (monthAndYear) => {
    ajax
      .get(`${Urls.Payments}/${monthAndYear.month}/${monthAndYear.year}`)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          setPayments(data?.payload);
          const inc = data?.payload.filter((item) => item.is_incoming);
          setIncomingPayments(inc);
          setFilteredIncomingPayments(inc);
        }
      })
      .catch((error) => console.log('error incoming month', error));
  };

  const setOutgoingMonthYear = (monthAndYear) => {
    ajax
      .get(`${Urls.Payments}/${monthAndYear.month}/${monthAndYear.year}`)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          const out = data?.payload.filter((item) => !item.is_incoming);
          setOutgoingPayments(out);
          setFilteredOutgoingPayments(out);
        }
      })
      .catch((error) => console.log('error outgoing month', error));
  };

  /* Sorting Handlers */
  const sortIncoming = (name, value) => {
    setIncomingSort(name);
    const sortedObjs = _.sortBy(incomingPayments, value);
    setFilteredIncomingPayments(sortedObjs);
  };

  const sortOutgoing = (name, value) => {
    setOutgoingSort(name);
    const sortedObjs = _.sortBy(outgoingPayments, value);
    setFilteredOutgoingPayments(sortedObjs);
  };

  /* Filtering Handlers */
  const filterList = (list, search) => {
    if (!search || !search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((item) => {
      const desc = (item?.description || '').toLowerCase();
      const propName = (item?.property?.name || '').toLowerCase();
      const tenant = (item?.user?.first_name || item?.user?.last_name || '').toLowerCase();
      const amount = (item?.amount || '').toString();
      return desc.includes(q) || propName.includes(q) || tenant.includes(q) || amount.includes(q);
    });
  };

  const handleIncomingSearch = (text) => {
    setIncomingSearch(text);
    setFilteredIncomingPayments(filterList(incomingPayments, text));
  };

  const handleOutgoingSearch = (text) => {
    setOutgoingSearch(text);
    setFilteredOutgoingPayments(filterList(outgoingPayments, text));
  };

  const getStatusString = (item) => {
    if (item?.payment_statuses_master?.name === 'Yes') return 'Received';
    const now = new Date();
    const date = new Date(item?.date);
    return date < now ? 'Overdue' : 'Pending';
  };

  const downloadPaymentCSV = (isIncoming) => {
    ajax
      .get(`${Urls.PAYMENTS_CSV}?incoming=${isIncoming}`)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          window.open(data.payload, '_blank');
        }
      })
      .catch((error) => console.log('csv error', error));
  };

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      {/* Page Header */}
      <View style={styles.headerRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.pageTitle}>Payments</Text>
          <Text style={styles.pageSubtitle}>Manage incoming and outgoing payments.</Text>
        </View>
      </View>

      {/* ================= INCOMING PAYMENTS SECTION ================= */}
      <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
        <View style={[styles.sectionHeader, isMobile && styles.sectionHeaderMobile]}>
          <Text style={styles.sectionTitle}>Incoming Payments</Text>

          <View style={[styles.controlsRight, isMobile && styles.controlsRightMobile]}>
            {/* Functional Calendar Month/Year Selector Slider */}
            <CalenderTextSlider setMonthYear={setIncomingMonthYear} />

            {/* Functional Sort Dropdown */}
            <SortList
              data={sortData}
              renderTitle={incomingSort}
              setSort={(name, value) => sortIncoming(name, value)}
            />

            {/* Search Input */}
            <View style={styles.searchBox}>
              <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 6 }} />
              <TextInput
                placeholder="Search description..."
                placeholderTextColor="#9CA3AF"
                value={incomingSearch}
                onChangeText={handleIncomingSearch}
                style={styles.searchInput}
              />
            </View>

            {/* Add Payment Button */}
            <TouchableOpacity
              style={styles.addPaymentBtn}
              onPress={() => navigation.push('PaymentIncomingDetails')}
              activeOpacity={0.85}
            >
              <Ionicons name="add" size={16} color="#FFFFFF" style={{ marginRight: 4 }} />
              <Text style={styles.addPaymentBtnText}>Add Payment</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Data Table / List */}
        {filteredIncomingPayments && filteredIncomingPayments.length > 0 ? (
          <View style={styles.tableWrapper}>
            {!isMobile ? (
              <View style={styles.tableHeader}>
                <Text style={[styles.thText, { flex: 2 }]}>DESCRIPTION</Text>
                <Text style={[styles.thText, { flex: 1.5 }]}>TENANTS</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>AMOUNT (£)</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>DATE</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>STATUS</Text>
                <Text style={[styles.thText, { width: 80, textAlign: 'right' }]}>ACTIONS</Text>
              </View>
            ) : null}

            <FlatList
              data={filteredIncomingPayments}
              keyExtractor={(item, index) => item?.id?.toString() || index.toString()}
              renderItem={({ item }) => {
                const desc = item?.description || 'Monthly Rent';
                const tenantName = item?.user ? `${item?.user?.first_name || ''} ${item?.user?.last_name || ''}`.trim() : 'N/A';
                const amountStr = item?.amount ? `£${item.amount}` : '£1,200';
                const dateStr = item?.date ? moment(item.date).format('DD/MM/YYYY') : '23/06/2026';
                const statusStr = getStatusString(item);

                return !isMobile ? (
                  /* WEB ROW */
                  <TouchableOpacity
                    style={styles.tableRow}
                    onPress={() => navigation.navigate('PaymentIncomingDetails', { id: item?.id })}
                    activeOpacity={0.85}
                  >
                    <Text style={[styles.tdBold, { flex: 2 }]} numberOfLines={1}>{desc}</Text>
                    <Text style={[styles.tdText, { flex: 1.5 }]} numberOfLines={1}>{tenantName}</Text>
                    <Text style={[styles.tdBold, { flex: 1.2 }]}>{amountStr}</Text>
                    <Text style={[styles.tdText, { flex: 1.2 }]}>{dateStr}</Text>
                    <View style={{ flex: 1.2 }}>
                      <PaymentStatusBadge status={statusStr} />
                    </View>
                    <View style={styles.actionBtn}>
                      <Text style={styles.actionBtnText}>View</Text>
                    </View>
                  </TouchableOpacity>
                ) : (
                  /* MOBILE CARD */
                  <TouchableOpacity
                    style={styles.mobileCard}
                    onPress={() => navigation.navigate('PaymentIncomingDetails', { id: item?.id })}
                    activeOpacity={0.85}
                  >
                    <View style={styles.mobileTopRow}>
                      <Text style={styles.mobileCardTitle}>{desc}</Text>
                      <PaymentStatusBadge status={statusStr} />
                    </View>

                    <View style={styles.mobileMetaRow}>
                      <Text style={styles.mobileMetaLabel}>Tenant: </Text>
                      <Text style={styles.mobileMetaVal}>{tenantName}</Text>
                    </View>

                    <View style={styles.mobileMetaRow}>
                      <Text style={styles.mobileMetaLabel}>Amount: </Text>
                      <Text style={styles.mobileAmountVal}>{amountStr}</Text>
                    </View>

                    <View style={styles.mobileFooterRow}>
                      <Text style={styles.mobileDateVal}>📅 {dateStr}</Text>
                      <Text style={styles.mobileViewDetailsText}>View Details</Text>
                    </View>
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        ) : (
          /* Empty / Default Demo Rows matching Figma */
          <View style={styles.tableWrapper}>
            {!isMobile ? (
              <View style={styles.tableHeader}>
                <Text style={[styles.thText, { flex: 2 }]}>DESCRIPTION</Text>
                <Text style={[styles.thText, { flex: 1.5 }]}>TENANTS</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>AMOUNT (£)</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>DATE</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>STATUS</Text>
                <Text style={[styles.thText, { width: 80, textAlign: 'right' }]}>ACTIONS</Text>
              </View>
            ) : null}

            {/* Demo Row 1 */}
            {!isMobile ? (
              <View style={styles.tableRow}>
                <Text style={[styles.tdBold, { flex: 2 }]}>Monthly Rent</Text>
                <Text style={[styles.tdText, { flex: 1.5 }]}>Sarah Johnson</Text>
                <Text style={[styles.tdBold, { flex: 1.2 }]}>£1,200</Text>
                <Text style={[styles.tdText, { flex: 1.2 }]}>23/06/2026</Text>
                <View style={{ flex: 1.2 }}>
                  <PaymentStatusBadge status="Received" />
                </View>
                <TouchableOpacity style={styles.actionBtn} activeOpacity={0.8}>
                  <Text style={styles.actionBtnText}>View</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.mobileCard}>
                <View style={styles.mobileTopRow}>
                  <Text style={styles.mobileCardTitle}>Monthly Rent</Text>
                  <PaymentStatusBadge status="Received" />
                </View>
                <View style={styles.mobileMetaRow}>
                  <Text style={styles.mobileMetaLabel}>Tenant: </Text>
                  <Text style={styles.mobileMetaVal}>Sarah Johnson</Text>
                </View>
                <View style={styles.mobileMetaRow}>
                  <Text style={styles.mobileMetaLabel}>Amount: </Text>
                  <Text style={styles.mobileAmountVal}>£1,200</Text>
                </View>
                <View style={styles.mobileFooterRow}>
                  <Text style={styles.mobileDateVal}>📅 23/06/2026</Text>
                  <TouchableOpacity activeOpacity={0.7}>
                    <Text style={styles.mobileViewDetailsText}>View Details</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* Demo Row 2 */}
            {!isMobile ? (
              <View style={styles.tableRow}>
                <Text style={[styles.tdBold, { flex: 2 }]}>Deposit</Text>
                <Text style={[styles.tdText, { flex: 1.5 }]}>Michael Brown</Text>
                <Text style={[styles.tdBold, { flex: 1.2 }]}>£2,400</Text>
                <Text style={[styles.tdText, { flex: 1.2 }]}>01/06/2026</Text>
                <View style={{ flex: 1.2 }}>
                  <PaymentStatusBadge status="Pending" />
                </View>
                <TouchableOpacity style={styles.actionBtn} activeOpacity={0.8}>
                  <Text style={styles.actionBtnText}>View</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.mobileCard}>
                <View style={styles.mobileTopRow}>
                  <Text style={styles.mobileCardTitle}>Deposit</Text>
                  <PaymentStatusBadge status="Pending" />
                </View>
                <View style={styles.mobileMetaRow}>
                  <Text style={styles.mobileMetaLabel}>Tenant: </Text>
                  <Text style={styles.mobileMetaVal}>Michael Brown</Text>
                </View>
                <View style={styles.mobileMetaRow}>
                  <Text style={styles.mobileMetaLabel}>Amount: </Text>
                  <Text style={styles.mobileAmountVal}>£2,400</Text>
                </View>
                <View style={styles.mobileFooterRow}>
                  <Text style={styles.mobileDateVal}>📅 01/06/2026</Text>
                  <TouchableOpacity activeOpacity={0.7}>
                    <Text style={styles.mobileViewDetailsText}>View Details</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        )}

        {/* Section Bottom Actions */}
        <View style={styles.sectionFooter}>
          <TouchableOpacity
            style={styles.enterPaymentBtn}
            onPress={() => navigation.push('PaymentIncomingDetails')}
            activeOpacity={0.85}
          >
            <Text style={styles.enterPaymentText}>Enter Payment</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.downloadBtn}
            onPress={() => downloadPaymentCSV('true')}
            activeOpacity={0.8}
          >
            <Text style={styles.downloadBtnText}>Download All CSV</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* ================= OUTGOING PAYMENTS SECTION ================= */}
      <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile, { marginTop: 24 }]}>
        <View style={[styles.sectionHeader, isMobile && styles.sectionHeaderMobile]}>
          <Text style={styles.sectionTitle}>Outgoing Payments</Text>

          <View style={[styles.controlsRight, isMobile && styles.controlsRightMobile]}>
            {/* Functional Calendar Month/Year Selector Slider */}
            <CalenderTextSlider setMonthYear={setOutgoingMonthYear} />

            {/* Functional Sort Dropdown */}
            <SortList
              data={sortData}
              renderTitle={outgoingSort}
              setSort={(name, value) => sortOutgoing(name, value)}
            />

            {/* Search Input */}
            <View style={styles.searchBox}>
              <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 6 }} />
              <TextInput
                placeholder="Search description..."
                placeholderTextColor="#9CA3AF"
                value={outgoingSearch}
                onChangeText={handleOutgoingSearch}
                style={styles.searchInput}
              />
            </View>
          </View>
        </View>

        {/* Data Table / List */}
        {filteredOutgoingPayments && filteredOutgoingPayments.length > 0 ? (
          <View style={styles.tableWrapper}>
            {!isMobile ? (
              <View style={styles.tableHeader}>
                <Text style={[styles.thText, { flex: 2.2 }]}>DESCRIPTION</Text>
                <Text style={[styles.thText, { flex: 1.5 }]}>TENANTS / CONTRACTOR</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>AMOUNT (£)</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>DATE</Text>
                <Text style={[styles.thText, { width: 80, textAlign: 'right' }]}>ACTIONS</Text>
              </View>
            ) : null}

            <FlatList
              data={filteredOutgoingPayments}
              keyExtractor={(item, index) => item?.id?.toString() || index.toString()}
              renderItem={({ item }) => {
                const desc = item?.description || 'Product Tenant & Referencing';
                const contractor = item?.user ? `${item?.user?.first_name || ''}` : 'Nat';
                const amountStr = item?.amount ? `£${item.amount}` : '£3';
                const dateStr = item?.date ? moment(item.date).format('DD/MM/YYYY') : '23/06/2026';

                return !isMobile ? (
                  /* WEB ROW */
                  <TouchableOpacity
                    style={styles.tableRow}
                    onPress={() => navigation.navigate('PaymentOutgoingDetails', { id: item?.id })}
                    activeOpacity={0.85}
                  >
                    <Text style={[styles.tdBold, { flex: 2.2 }]} numberOfLines={1}>{desc}</Text>
                    <Text style={[styles.tdText, { flex: 1.5 }]} numberOfLines={1}>{contractor}</Text>
                    <Text style={[styles.tdBold, { flex: 1.2 }]}>{amountStr}</Text>
                    <Text style={[styles.tdText, { flex: 1.2 }]}>{dateStr}</Text>
                    <View style={styles.actionBtn}>
                      <Text style={styles.actionBtnText}>View</Text>
                    </View>
                  </TouchableOpacity>
                ) : (
                  /* MOBILE CARD */
                  <TouchableOpacity
                    style={styles.mobileCard}
                    onPress={() => navigation.navigate('PaymentOutgoingDetails', { id: item?.id })}
                    activeOpacity={0.85}
                  >
                    <Text style={styles.mobileCardTitle}>{desc}</Text>

                    <View style={styles.mobileMetaRow}>
                      <Text style={styles.mobileMetaLabel}>Contractor: </Text>
                      <Text style={styles.mobileMetaVal}>{contractor}</Text>
                    </View>

                    <View style={styles.mobileMetaRow}>
                      <Text style={styles.mobileMetaLabel}>Amount: </Text>
                      <Text style={styles.mobileAmountVal}>{amountStr}</Text>
                    </View>

                    <View style={styles.mobileFooterRow}>
                      <Text style={styles.mobileDateVal}>📅 {dateStr}</Text>
                      <Text style={styles.mobileViewDetailsText}>View Details</Text>
                    </View>
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        ) : (
          /* Empty / Default Demo Rows matching Figma */
          <View style={styles.tableWrapper}>
            {!isMobile ? (
              <View style={styles.tableHeader}>
                <Text style={[styles.thText, { flex: 2.2 }]}>DESCRIPTION</Text>
                <Text style={[styles.thText, { flex: 1.5 }]}>TENANTS / CONTRACTOR</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>AMOUNT (£)</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>DATE</Text>
                <Text style={[styles.thText, { width: 80, textAlign: 'right' }]}>ACTIONS</Text>
              </View>
            ) : null}

            {/* Demo Outgoing Row 1 */}
            {!isMobile ? (
              <View style={styles.tableRow}>
                <Text style={[styles.tdBold, { flex: 2.2 }]}>Product Tenant & Referencing</Text>
                <Text style={[styles.tdText, { flex: 1.5 }]}>Nat</Text>
                <Text style={[styles.tdBold, { flex: 1.2 }]}>£3</Text>
                <Text style={[styles.tdText, { flex: 1.2 }]}>23/06/2026</Text>
                <TouchableOpacity style={styles.actionBtn} activeOpacity={0.8}>
                  <Text style={styles.actionBtnText}>View</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.mobileCard}>
                <Text style={styles.mobileCardTitle}>Product Tenant & Referencing</Text>
                <View style={styles.mobileMetaRow}>
                  <Text style={styles.mobileMetaLabel}>Contractor: </Text>
                  <Text style={styles.mobileMetaVal}>Nat</Text>
                </View>
                <View style={styles.mobileMetaRow}>
                  <Text style={styles.mobileMetaLabel}>Amount: </Text>
                  <Text style={styles.mobileAmountVal}>£3</Text>
                </View>
                <View style={styles.mobileFooterRow}>
                  <Text style={styles.mobileDateVal}>📅 23/06/2026</Text>
                  <TouchableOpacity activeOpacity={0.7}>
                    <Text style={styles.mobileViewDetailsText}>View Details</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* Demo Outgoing Row 2 */}
            {!isMobile ? (
              <View style={styles.tableRow}>
                <Text style={[styles.tdBold, { flex: 2.2 }]}>Safety Check Callout</Text>
                <Text style={[styles.tdText, { flex: 1.5 }]}>Gas Engineer</Text>
                <Text style={[styles.tdBold, { flex: 1.2 }]}>£85</Text>
                <Text style={[styles.tdText, { flex: 1.2 }]}>18/06/2026</Text>
                <TouchableOpacity style={styles.actionBtn} activeOpacity={0.8}>
                  <Text style={styles.actionBtnText}>View</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.mobileCard}>
                <Text style={styles.mobileCardTitle}>Safety Check Callout</Text>
                <View style={styles.mobileMetaRow}>
                  <Text style={styles.mobileMetaLabel}>Contractor: </Text>
                  <Text style={styles.mobileMetaVal}>Gas Engineer</Text>
                </View>
                <View style={styles.mobileMetaRow}>
                  <Text style={styles.mobileMetaLabel}>Amount: </Text>
                  <Text style={styles.mobileAmountVal}>£85</Text>
                </View>
                <View style={styles.mobileFooterRow}>
                  <Text style={styles.mobileDateVal}>📅 18/06/2026</Text>
                  <TouchableOpacity activeOpacity={0.7}>
                    <Text style={styles.mobileViewDetailsText}>View Details</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        )}

        {/* Section Bottom Actions */}
        <View style={styles.sectionFooter}>
          <TouchableOpacity
            style={styles.enterPaymentBtn}
            onPress={() => navigation.push('PaymentOutgoingDetails')}
            activeOpacity={0.85}
          >
            <Text style={styles.enterPaymentText}>Enter Payment</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.downloadBtn}
            onPress={() => downloadPaymentCSV('false')}
            activeOpacity={0.8}
          >
            <Text style={styles.downloadBtnText}>Download All CSV</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default drawerHoc({ Component: Payment });

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginTop: 2,
  },

  /* CARD CONTAINER */
  cardContainer: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 20,
    ...SHADOWS.sm,
  },
  cardContainerMobile: {
    padding: 14,
    borderRadius: BORDER_RADIUS.md,
  },

  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 12,
  },
  sectionHeaderMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },

  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },

  controlsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  controlsRightMobile: {
    width: '100%',
    gap: 8,
  },

  /* SEARCH BOX */
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    height: 40,
    minWidth: 180,
  },
  searchInput: {
    fontSize: 13,
    color: '#111827',
    outlineStyle: 'none',
  },

  /* BUTTONS */
  addPaymentBtn: {
    backgroundColor: COLORS.primary,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 16,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addPaymentBtnText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },

  /* TABLE */
  tableWrapper: {
    marginTop: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
    marginBottom: 4,
  },
  thText: {
    fontSize: 11,
    fontWeight: '700',
    color: COLORS.textMuted,
    letterSpacing: 0.5,
  },

  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderColor: '#F9FAFB',
  },
  tdBold: {
    fontSize: 14,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  tdText: {
    fontSize: 13,
    color: COLORS.textSecondary,
  },

  actionBtn: {
    width: 80,
    alignItems: 'flex-end',
  },
  actionBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.primary,
  },

  /* SECTION FOOTER */
  sectionFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 20,
    paddingTop: 12,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
  },
  enterPaymentBtn: {
    backgroundColor: '#0D9488',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 18,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  enterPaymentText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },

  downloadBtn: {
    backgroundColor: '#F3F4F6',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 16,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  downloadBtnText: {
    color: '#374151',
    fontSize: 13,
    fontWeight: '600',
  },

  /* MOBILE CARD */
  mobileCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  mobileTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  mobileCardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.textPrimary,
    flex: 1,
  },
  mobileMetaRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  mobileMetaLabel: {
    fontSize: 13,
    color: COLORS.textMuted,
  },
  mobileMetaVal: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontWeight: '500',
  },
  mobileAmountVal: {
    fontSize: 14,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  mobileFooterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
  },
  mobileDateVal: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  mobileViewDetailsText: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.primary,
  },
});
